﻿function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      const previouslySelected = Array.from(target.options).find((option) =>
        option.getAttribute("selected")
      );
      previouslySelected?.removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

// Localiza links internos para o idioma atual (prefixo `routes.root_url`).
// NecessÃ¡rio para links hardcoded em rich-text dentro de templates JSON,
// onde Liquid nÃ£o Ã© processado.
(function localizeInternalLinksToCurrentLocale() {
  const rootUrl = (window.routes && window.routes.root_url) || "/";
  if (!rootUrl || rootUrl === "/") return;

  const rootPrefix = rootUrl.endsWith("/") ? rootUrl.slice(0, -1) : rootUrl; // ex: "/en" | "/es"
  const currentOrigin = window.location.origin;
  const currentHost = window.location.host;

  const allowedHosts = new Set([
    window.location.hostname,
    "maioricasa.com.br",
    "www.maioricasa.com.br",
  ]);
  const internalPrefixes = [
    "/pages/",
    "/collections/",
    "/blogs/",
    "/products/",
    "/account",
    "/cart",
    "/search",
    "/policies/",
  ];

  const isAlreadyLocalized = (pathname) =>
    pathname === rootPrefix || pathname.startsWith(`${rootPrefix}/`);

  document.querySelectorAll("a[href]").forEach((anchor) => {
    const rawHref = anchor.getAttribute("href");
    if (!rawHref) return;

    const trimmedHref = rawHref.trim();
    if (
      trimmedHref === "" ||
      trimmedHref.startsWith("#") ||
      trimmedHref.startsWith("mailto:") ||
      trimmedHref.startsWith("tel:") ||
      trimmedHref.startsWith("sms:") ||
      trimmedHref.startsWith("javascript:") ||
      trimmedHref.startsWith("data:")
    )
      return;

    let url;
    try {
      if (trimmedHref.startsWith("/")) {
        url = new URL(trimmedHref, currentOrigin);
      } else if (
        trimmedHref.startsWith("http://") ||
        trimmedHref.startsWith("https://")
      ) {
        url = new URL(trimmedHref);
        if (!allowedHosts.has(url.hostname)) return;

        // Se o link aponta para o domÃ­nio de produÃ§Ã£o, mantÃ©m o usuÃ¡rio no ambiente atual (preview/dev).
        url.host = currentHost;
      } else {
        // Ex: "onde-comprar" (relativo sem /) â€” nÃ£o reescreve para evitar falsos positivos
        return;
      }
    } catch {
      return;
    }

    if (isAlreadyLocalized(url.pathname)) return;

    const shouldRewrite = internalPrefixes.some(
      (p) => url.pathname === p || url.pathname.startsWith(p)
    );
    if (!shouldRewrite) return;

    url.pathname = `${rootPrefix}${url.pathname}`;
    anchor.href = url.toString();
  });
})();

// Exibe tamanhos imperiais nos filtros de coleÃ§Ã£o (EN), mantendo o valor real do filtro (mÃ©trico).
(function localizeSizeFacetLabels() {
  const rootUrl = (window.routes && window.routes.root_url) || "/";
  const lang = (
    (document.documentElement && document.documentElement.lang) ||
    ""
  ).toLowerCase();
  const isEnglish =
    rootUrl.toLowerCase().startsWith("/en") ||
    lang === "en" ||
    lang.startsWith("en-");
  const debug = new URLSearchParams(window.location.search).has("debug_sizes");
  if (!isEnglish && !debug) return;

  let isApplying = false;
  let scheduled = false;

  const normalizeSizeKey = (value) =>
    String(value || "")
      .trim()
      .replace(/Ã—/g, "x")
      .replace(/\s*x\s*/gi, " x ")
      .replace(/\s+/g, " ");

  function formatImperialFromCm(cm) {
    const numericCm = Number(cm);
    if (!Number.isFinite(numericCm) || numericCm <= 0) return null;

    // ConversÃ£o: truncar para polegada inteira (nÃ£o arredonda para cima),
    // para manter padrÃ£o tipo 6'6" x 8'2".
    // Epsilon evita casos como 98.0000000003 virar 97 por causa de float.
    const totalInchesTruncated = Math.floor(numericCm / 2.54 + 1e-9);
    const feet = Math.floor(totalInchesTruncated / 12);
    const inches = totalInchesTruncated % 12;

    if (feet <= 0) return null;
    if (inches === 0) return `${feet}'`;
    return `${feet}'${inches}"`;
  }

  function computeImperialFromMetricLabel(label) {
    const normalized = normalizeSizeKey(label);
    const match = normalized.match(
      /^(\d+(?:[.,]\d+)?)\s*x\s*(\d+(?:[.,]\d+)?)/i
    );
    if (!match) return null;

    const left = match[1].replace(",", ".");
    const right = match[2].replace(",", ".");
    const a = formatImperialFromCm(left);
    const b = formatImperialFromCm(right);
    if (!a || !b) return null;

    return `${a} x ${b}`;
  }

  function apply(root) {
    const map = window.__imperialSizeByOptionValue || {};
    const hasMap =
      map && typeof map === "object" && Object.keys(map).length > 0;
    if (debug) {
      console.log("[debug_sizes] localizeSizeFacetLabels", {
        rootUrl,
        lang,
        isEnglish,
        mapKeys: Object.keys(map).length,
      });
    }
    const normalizedMap = {};
    if (hasMap) {
      Object.keys(map).forEach((k) => {
        const normalizedKey = normalizeSizeKey(k);
        if (normalizedKey && !normalizedMap[normalizedKey])
          normalizedMap[normalizedKey] = map[k];
      });
    }

    const detailsNodes = root.querySelectorAll(
      '.js-filter[id*="tamanho"], .js-filter[id*="size"]'
    );
    if (debug) console.log("[debug_sizes] detailsNodes", detailsNodes.length);
    detailsNodes.forEach((details) => {
      const labels = details.querySelectorAll(".facet-checkbox__text-label");
      if (debug)
        console.log(
          "[debug_sizes] labels in details",
          labels.length,
          details.id
        );
      labels.forEach((labelEl) => {
        const original =
          labelEl.getAttribute("data-original-label") ||
          labelEl.textContent.trim();
        if (!labelEl.hasAttribute("data-original-label"))
          labelEl.setAttribute("data-original-label", original);

        const imperialFromMap =
          map[original] || normalizedMap[normalizeSizeKey(original)];
        const imperialComputed = computeImperialFromMetricLabel(original);
        const imperial = imperialFromMap || imperialComputed;

        if (!imperial) {
          if (debug)
            console.log("[debug_sizes] no imperial match for", original);
          return;
        }

        const desired = `${original} (${imperial})`;
        if (labelEl.textContent.trim() === desired) return;
        labelEl.textContent = desired;
      });
    });
  }

  function applyAll() {
    const desktop = document.getElementById("FacetFiltersForm");
    const mobile = document.getElementById("FacetFiltersFormMobile");
    if (desktop) apply(desktop);
    if (mobile) apply(mobile);
  }

  applyAll();

  const targets = [];
  const desktopTarget = document.getElementById("FacetFiltersForm");
  const mobileTarget = document.getElementById("FacetFiltersFormMobile");
  if (desktopTarget) targets.push(desktopTarget);
  if (mobileTarget) targets.push(mobileTarget);

  if (targets.length === 0) return;

  const scheduleApply = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      if (isApplying) return;
      isApplying = true;
      try {
        applyAll();
      } finally {
        isApplying = false;
      }
    });
  };

  const observer = new MutationObserver(() => scheduleApply());
  targets.forEach((t) =>
    observer.observe(t, { childList: true, subtree: true })
  );
})();

// Normaliza labels de filtros no Espanhol quando vierem crus do Search & Discovery.
(function localizeSpanishFacetLabels() {
  const rootUrl = (window.routes && window.routes.root_url) || "/";
  const lang = (
    (document.documentElement && document.documentElement.lang) ||
    ""
  ).toLowerCase();
  const isSpanish =
    rootUrl.toLowerCase().startsWith("/es") ||
    lang === "es" ||
    lang.startsWith("es-");
  if (!isSpanish) return;

  const normalizeKey = (value) =>
    String(value || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim();

    const labelMap = {
    application: "Aplicación",
    aplicacao: "Aplicación",
    "type of use": "Tipo de uso",
    "tipo de uso": "Tipo de uso",
    intensity: "Intensidad",
    intensidade: "Intensidad",
    performances: "Performances",
    material: "Material",
    buildings: "Construcciones",
    construcoes: "Construcciones",
    size: "Tamaño",
    tamanho: "Tamaño",
    styles: "Estilos",
    estilos: "Estilos",
  };

  const valueMap = {
    indoor: "Interior",
    outdoor: "Exterior",
    "outdoor & indoor": "Exterior e interior",
    "outdoor/indoor": "Exterior e interior",
    "outdoor-indoor": "Exterior e interior",
    light: "Ligero",
    leve: "Ligero",
    moderate: "Moderado",
    moderado: "Moderado",
    high: "Alto",
    heavy: "Alto",
    "uso intenso": "Uso intenso",
    residencial: "Residencial",
    residential: "Residencial",
    comercial: "Comercial",
    commercial: "Comercial",
    corporativo: "Corporativo",
    "tecido para cortina": "Tejido para cortina",
    "tailor-made": "Hecho a medida",
    "sob medida": "Hecho a medida",
    "a medida": "Hecho a medida",
    "feito a medida": "Hecho a medida",
    rugs: "Alfombras",
    tapetes: "Alfombras",
    fabrics: "Tejidos",
    tecidos: "Tejidos",
    "amigavel aos pets": "Apto para mascotas",
    "pet friendly": "Apto para mascotas",
    antiacaro: "Antiácaros",
    antimofo: "Antimoho",
    "feito com fio de pet reciclado": "Hecho con hilo PET reciclado",
    "100 fio de pet reciclado": "100% hilo de PET reciclado",
    "limpeza facil": "Limpieza fácil",
    "limpeza total com agua": "Limpieza total con agua",
    "resistencia uva/uvb": "Resistencia UVA/UVB",
    "resistencia uva uvb": "Resistencia UVA/UVB",
    "resistencia uva-uvb": "Resistencia UVA/UVB",
    "resistencia a manchas": "Resistencia a manchas",
    "repelencia a liquidos": "Repelencia a líquidos",
    "repelencia a insetos": "Repelencia a insectos",
    impermeabilidade: "Impermeabilidad",
    "retardante de chamas": "Retardante de llama",
    "flame retardant": "Retardante de llama",
    "blindagem de espuma": "Blindaje de espuma",
    "blindagem da espuma": "Blindaje de espuma",
    "alta performance": "Alto desempeño",
    "alto trafego": "Alto tráfico",
    "reutilizacao sustentavel": "Reutilización sostenible",
    algodao: "Algodón",
    cotton: "Algodón",
    "fio de pet reciclado": "Hilo de PET reciclado",
    acrilico: "Acrílico",
    acrylic: "Acrílico",
    linho: "Lino",
    linen: "Lino",
    juta: "Yute",
    jute: "Yute",
    "juta soumak": "Yute Soumak",
    la: "Lana",
    wool: "Lana",
    "la neozelandesa": "Lana neozelandesa",
    poliester: "Poliéster",
    polyester: "Poliéster",
    polipropileno: "Polipropileno",
    polypropylene: "Polipropileno",
    viscose: "Viscosa",
    nylon: "Nailon",
    tencel: "Tencel",
    ecose: "Ecose",
    boucle: "Bouclé",
    elastano: "Elastano",
    jacquard: "Jacquard",
    liso: "Liso",
    listrado: "Rayado",
    veludo: "Terciopelo",
    tramado: "Tramado",
    vinilico: "Vinílico",
    "hand loomed": "Hand Loomed",
    "hand tufted": "Hand Tufted",
    "hand woven": "Hand Woven",
    "hand braided": "Hand Braided",
    "hand knotted": "Hand Knotted",
    "jacquard woven": "Jacquard Woven",
    "power loomed": "Power Loomed",
    chevron: "Chevron",
    palha: "Paja",
    sarja: "Sarga",
    "tons de cinza": "Tonos de gris",
    "tons de bege": "Tonos de beige",
    colorido: "Colorido",
    branco: "Blanco",
    azul: "Azul",
    verde: "Verde",
    rosa: "Rosa",
    aubergine: "Aubergine",
    marrom: "Marrón",
    preto: "Negro",
    alaranjado: "Anaranjado",
    amarelo: "Amarillo",
    terroso: "Terroso",
    vermelho: "Rojo",
    cinza: "Gris",
    bege: "Beige",
    india: "India",
    "índia": "India",
    china: "China",
    belgica: "Bélgica",
    "bélgica": "Bélgica",
    turquia: "Turquía",
    egito: "Egipto",
    "reach svhv": "REACH SVHV",
    sgs: "SGS",
    "oeko-tex": "OEKO-TEX",
    intertek: "Intertek",
    stc: "STC",
  };

  const normalizeMapKeys = (sourceMap) => {
    const normalizedMap = {};
    Object.entries(sourceMap).forEach(([key, value]) => {
      normalizedMap[normalizeKey(key)] = value;
    });
    return normalizedMap;
  };

  const normalizedLabelMap = normalizeMapKeys(labelMap);
  const normalizedValueMap = normalizeMapKeys(valueMap);

  const translateText = (input, map) => {
    const text = String(input || "").trim();
    if (!text) return text;
    const normalized = normalizeKey(text);
    return map[normalized] || text;
  };

  const updateNodeText = (node, map) => {
    if (!node) return;
    const current = node.textContent.trim();
    if (!node.dataset.originalLabel) node.dataset.originalLabel = current;
    const translated = translateText(current, map);
    if (translated !== current) node.textContent = translated;
  };

  const parseFacetCount = (text) => {
    const match = String(text || "").match(/\((\d+)\)\s*$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const setFacetLabelAndCount = (labelNode, labelText, count) => {
    if (!labelNode) return;
    const textWrapper = labelNode.closest(".facet-checkbox__text");
    if (!textWrapper || !Number.isFinite(count) || count <= 0) return;
    const currentLabel = labelNode.textContent.trim();
    const currentCount = parseFacetCount(textWrapper.textContent || "");
    if (currentLabel === labelText && currentCount === count) return;

    textWrapper.textContent = "";
    const span = document.createElement("span");
    span.className = "facet-checkbox__text-label";
    span.textContent = labelText;
    textWrapper.appendChild(span);
    textWrapper.appendChild(document.createTextNode(` (${count})`));
  };

  const dedupeTranslatedFacetValues = (root) => {
    const facetDetails = root.querySelectorAll(
      "details.facets__disclosure-vertical, details.mobile-facets__details, details.facets__disclosure, details.disclosure-has-popup"
    );
    facetDetails.forEach((detailsEl) => {
      const labels = Array.from(
        detailsEl.querySelectorAll(".facet-checkbox__text-label")
      );
      if (!labels.length) return;

      const byTranslatedLabel = new Map();
      labels.forEach((labelNode) => {
        const translatedLabel = labelNode.textContent.trim();
        if (!translatedLabel) return;
        const itemNode = labelNode.closest(".facets__label");
        if (!itemNode) return;
        const inputNode = itemNode.querySelector('input[type="checkbox"]');
        const count = parseFacetCount(
          labelNode.closest(".facet-checkbox__text")?.textContent || ""
        );
        const item = { labelNode, itemNode, inputNode, count, translatedLabel };
        const normalizedLabel = normalizeKey(translatedLabel);
        if (!byTranslatedLabel.has(normalizedLabel))
          byTranslatedLabel.set(normalizedLabel, []);
        byTranslatedLabel.get(normalizedLabel).push(item);
      });

      byTranslatedLabel.forEach((group) => {
        if (group.length < 2) return;

        const primary = group[0];
        const translatedLabel = primary.translatedLabel;
        const duplicates = group.slice(1);
        const totalCount = group.reduce(
          (sum, it) => sum + (Number.isFinite(it.count) ? it.count : 0),
          0
        );

        if (totalCount > 0)
          setFacetLabelAndCount(primary.labelNode, translatedLabel, totalCount);

        duplicates.forEach((dup) => {
          if (dup.itemNode) dup.itemNode.style.display = "none";
        });

        if (
          primary.inputNode &&
          duplicates.length &&
          !primary.inputNode.dataset.dupSyncBound
        ) {
          const duplicateInputs = duplicates
            .map((dup) => dup.inputNode)
            .filter(Boolean);
          primary.inputNode.addEventListener("change", () => {
            duplicateInputs.forEach((dupInput) => {
              dupInput.checked = primary.inputNode.checked;
            });
          });
          primary.inputNode.dataset.dupSyncBound = "true";
        }
      });
    });
  };

  const apply = (root) => {
    root
      .querySelectorAll(".facets__summary-label")
      .forEach((node) => updateNodeText(node, normalizedLabelMap));
    root
      .querySelectorAll(".mobile-facets__summary > div > span")
      .forEach((node) => updateNodeText(node, normalizedLabelMap));
    root
      .querySelectorAll(".facet-checkbox__text-label")
      .forEach((node) => updateNodeText(node, normalizedValueMap));
    dedupeTranslatedFacetValues(root);
  };

  const applyAll = () => {
    const desktop = document.getElementById("FacetFiltersForm");
    const mobile = document.getElementById("FacetFiltersFormMobile");
    if (desktop) apply(desktop);
    if (mobile) apply(mobile);
  };

  applyAll();
  setTimeout(applyAll, 200);
  document.addEventListener("facets:rendered", applyAll);
})();

// Ajusta link da campanha de blocos 3D conforme idioma atual
(function localizeCampanhaBlocos3DLink() {
  const applyCampanhaLink = () => {
    const campanhaLinks = document.querySelectorAll(
      'a[href*="#blocos3d-campanha"]'
    );
    if (!campanhaLinks.length) return;

    const htmlLang = (document.documentElement.lang || "").toLowerCase();
    const rootUrl = (
      (window.routes && window.routes.root_url) ||
      "/"
    ).toLowerCase();

    let url = "https://campanha.maioricasa.com.br/blocos3d";

    if (htmlLang.includes("es") || rootUrl.startsWith("/es")) {
      url = "https://campanha.maioricasa.com.br/esp-blocos3d";
    } else if (htmlLang.includes("en") || rootUrl.startsWith("/en")) {
      url = "https://campanha.maioricasa.com.br/eng-blocos3d";
    }

    campanhaLinks.forEach((campanhaLink) => {
      campanhaLink.setAttribute("href", url);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", applyCampanhaLink, {
      once: true,
    });
  } else {
    applyCampanhaLink();
  }
})();

// Safeguard para evitar spinner preso nos filtros da colecao
(function preventStuckFacetLoading() {
  const clearFacetLoadingState = () => {
    document
      .querySelectorAll(
        ".product-count-vertical .loading__spinner, .product-count .loading__spinner"
      )
      .forEach((spinner) => {
        spinner.classList.add("hidden");
      });

    const productCountDesktop = document.getElementById("ProductCountDesktop");
    const productCountMobile = document.getElementById("ProductCount");
    if (productCountDesktop) productCountDesktop.classList.remove("loading");
    if (productCountMobile) productCountMobile.classList.remove("loading");

    const collectionNode = document.querySelector(
      "#ProductGridContainer .collection"
    );
    if (collectionNode) collectionNode.classList.remove("loading");
  };

  document.addEventListener("facets:rendered", clearFacetLoadingState);

  document.addEventListener("change", (event) => {
    if (!event.target || !event.target.closest("facet-filters-form")) return;
    setTimeout(clearFacetLoadingState, 2500);
  });

  document.addEventListener("click", (event) => {
    const target =
      event.target && event.target.closest
        ? event.target.closest(
            ".active-facets__button, .facets__reset, .mobile-facets__clear, facet-remove a"
          )
        : null;
    if (!target) return;
    setTimeout(clearFacetLoadingState, 2500);
  });
})();

// CÃ³digo para abrir links externos em uma nova aba
const links = document.querySelectorAll("a");
for (let i = 0; i < links.length; i++) {
  if (links[i].hostname !== window.location.hostname) {
    links[i].target = "_blank";
    links[i].rel = "noreferrer noopener"; // Boa prÃ¡tica de seguranÃ§a e performance
  }
}

